import { AnalyticsService } from '@alison/analytics/client';
import { useTenant } from '@alison/whitelabel/react';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import type { ReactNode } from 'react';
import { TOGGLE_BUTTON_SX } from '../creatives/utils';
import { TenantLogo } from '../TenantLogo';
import type { ElementsTableBase } from './data/api';
import type { Scene } from './data/elementsTable';

type ActiveScene = Exclude<Scene, 'all'>;

export function ElementsTable({
  activeBase,
  onActiveBaseChange,
  activeScenes,
  onActiveScenesChange,
  metricsControl,
  children,
}: {
  activeBase: ElementsTableBase;
  onActiveBaseChange: (base: ElementsTableBase) => void;
  activeScenes: ActiveScene[];
  onActiveScenesChange: (scenes: ActiveScene[]) => void;
  metricsControl?: ReactNode;
  children: ReactNode;
}): ReactNode {
  const { picksLabel } = useTenant();
  function toggleScene(scene: ActiveScene): void {
    onActiveScenesChange(
      activeScenes.includes(scene)
        ? activeScenes.filter((entry) => entry !== scene)
        : [...activeScenes, scene],
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          value={[activeBase, ...activeScenes]}
          sx={{ backgroundColor: 'background.page' }}
        >
          <ToggleButton
            value="all"
            sx={TOGGLE_BUTTON_SX}
            onClick={() => {
              AnalyticsService.track('Elements Explorer - Filter Tab', { tab: 'all' });
              onActiveBaseChange('all');
            }}
          >
            All
          </ToggleButton>
          <ToggleButton
            value="alison-picks"
            sx={{ gap: 1, ...TOGGLE_BUTTON_SX }}
            onClick={() => {
              AnalyticsService.track('Elements Explorer - Filter Tab', { tab: 'alison-picks' });
              onActiveBaseChange('alison-picks');
            }}
          >
            <TenantLogo variant="glyph" height={14} />
            {picksLabel} Picks
          </ToggleButton>
          <ToggleButton
            value="hook-scene"
            sx={TOGGLE_BUTTON_SX}
            onClick={() => {
              AnalyticsService.track('Elements Explorer - Filter Tab', { tab: 'hook-scene' });
              toggleScene('hook-scene');
            }}
          >
            Hook Scene
          </ToggleButton>
          <ToggleButton
            value="end-card"
            sx={TOGGLE_BUTTON_SX}
            onClick={() => {
              AnalyticsService.track('Elements Explorer - Filter Tab', { tab: 'end-card' });
              toggleScene('end-card');
            }}
          >
            End Card
          </ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ ml: 'auto' }}>{metricsControl}</Box>
      </Box>
      {children}
    </Box>
  );
}
